import { makeStyles } from '@material-ui/core';

import { useDisplayNames } from '@spotify/confidence-core-react';
import { ChangeSetFragment } from '@spotify/confidence-graphql';
import {
  ActivityChangeOptions,
  ActivityItem,
  ActivityTagItem,
} from '@spotify/plugin-confidence-activity-react';

import {
  type MetricsData,
  formatBucket,
  formatDirection,
  formatMDE,
  formatNIM,
  getMetricId,
  getMetricTypeLabel,
  isGuardrailMetric,
  isSuccessMetric,
  useMetricsChangeDiff,
} from '../hooks/useMetricsChangeDiff';
import { createWorkflowUpdatesChangeExtension } from './workflow-updates-change';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    gap: 'var(--encore-spacing-tighter-4)',
  },
});

const MetricsChangeRender = ({ change }: ActivityChangeOptions) => {
  const classes = useStyles();
  const { displayNames } = useDisplayNames();

  const oldValue = change.oldValue as MetricsData | null;
  const newValue = change.newValue as MetricsData | null;

  const {
    addedMetrics,
    removedMetrics,
    kindChanges,
    mdeChanges,
    nimChanges,
    directionChanges,
    addedFilters,
    removedFilters,
    scheduleChanges,
    surfaceOnlyChanges,
    bucketChanged,
    entityChanged,
    assignmentTableChanged,
    exposureMetricChanged,
    exposureTableChanged,
  } = useMetricsChangeDiff(oldValue, newValue);

  return (
    <>
      {addedMetrics.length > 0 &&
        addedMetrics.map(metric => {
          const metricTypeLabel = getMetricTypeLabel(metric.metricRole);
          const id = getMetricId(metric.metric);
          const displayName = displayNames.get(id) || id;

          return (
            <ActivityItem.Section key={id} label={`Added ${metricTypeLabel}`}>
              <ActivityTagItem tagLabel={displayName} ariaLabel="Metric name" />
              {isGuardrailMetric(metric) &&
                metric.metricRole?.nonInferiorityMargin !== undefined && (
                  <ActivityTagItem
                    title="Non-inferiority margin"
                    newValue={formatNIM(metric.metricRole.nonInferiorityMargin)}
                    ariaLabel="Non-inferiority margin"
                  />
                )}
              {isSuccessMetric(metric) &&
                metric.metricRole?.minimumDetectableEffect !== undefined && (
                  <ActivityTagItem
                    title="MDE"
                    newValue={formatMDE(
                      metric.metricRole.minimumDetectableEffect,
                    )}
                    ariaLabel="Minimum detectable effect"
                  />
                )}
              <ActivityTagItem
                title="Preferred direction"
                newValue={formatDirection(metric.preferredDirection)}
                ariaLabel="Preferred direction"
              />
            </ActivityItem.Section>
          );
        })}

      {removedMetrics.length > 0 &&
        removedMetrics.map(metric => {
          const metricTypeLabel = getMetricTypeLabel(metric.metricRole);
          const id = getMetricId(metric.metric);
          const displayName = displayNames.get(id) || id;

          return (
            <ActivityItem.Section key={id} label={`Removed ${metricTypeLabel}`}>
              <ActivityTagItem oldValue={displayName} ariaLabel="Metric name" />
            </ActivityItem.Section>
          );
        })}

      {kindChanges.length > 0 && (
        <ActivityItem.Section label="Updated metric role">
          {kindChanges.map(({ oldMetric, newMetric, id }) => (
            <ActivityTagItem
              key={id}
              tagLabel={displayNames.get(id) || id}
              ariaLabel="Metric role change"
              oldValue={getMetricTypeLabel(oldMetric.metricRole) || 'Unknown'}
              newValue={getMetricTypeLabel(newMetric.metricRole) || 'Unknown'}
            />
          ))}
        </ActivityItem.Section>
      )}

      {mdeChanges.length > 0 && (
        <ActivityItem.Section label="Updated metrics MDE">
          {mdeChanges.map(({ oldMetric, newMetric, id }) => (
            <ActivityTagItem
              key={id}
              tagLabel={displayNames.get(id) || id}
              ariaLabel="Minimum detectable effect change"
              oldValue={formatMDE(
                oldMetric.metricRole?.minimumDetectableEffect,
              )}
              newValue={formatMDE(
                newMetric.metricRole?.minimumDetectableEffect,
              )}
            />
          ))}
        </ActivityItem.Section>
      )}

      {nimChanges.length > 0 && (
        <ActivityItem.Section label="Updated non-inferiority margin">
          {nimChanges.map(({ oldMetric, newMetric, id }) => (
            <ActivityTagItem
              key={id}
              tagLabel={displayNames.get(id) || id}
              ariaLabel="Non-inferiority margin change"
              oldValue={formatNIM(oldMetric.metricRole?.nonInferiorityMargin)}
              newValue={formatNIM(newMetric.metricRole?.nonInferiorityMargin)}
            />
          ))}
        </ActivityItem.Section>
      )}

      {directionChanges.length > 0 && (
        <ActivityItem.Section label="Updated metrics direction">
          {directionChanges.map(({ oldMetric, newMetric, id }) => (
            <ActivityTagItem
              key={id}
              tagLabel={displayNames.get(id) || id}
              ariaLabel="Preferred direction change"
              oldValue={formatDirection(oldMetric.preferredDirection)}
              newValue={formatDirection(newMetric.preferredDirection)}
            />
          ))}
        </ActivityItem.Section>
      )}

      {addedFilters.length > 0 && (
        <ActivityItem.Section label="Added metrics filter">
          <div className={classes.container}>
            {addedFilters.map(filter => (
              <ActivityTagItem
                key={filter.displayName}
                tagLabel={filter.displayName || 'Unnamed filter'}
                ariaLabel="Exposure filter name"
              />
            ))}
          </div>
        </ActivityItem.Section>
      )}

      {removedFilters.length > 0 && (
        <ActivityItem.Section label="Removed metrics filter">
          <div className={classes.container}>
            {removedFilters.map(filter => (
              <ActivityTagItem
                key={filter.displayName}
                tagLabel={filter.displayName || 'Unnamed filter'}
                ariaLabel="Exposure filter name"
              />
            ))}
          </div>
        </ActivityItem.Section>
      )}

      {bucketChanged && (
        <ActivityItem.Section label="Updated calculation interval">
          <ActivityTagItem
            oldValue={formatBucket(oldValue?.bucket)}
            newValue={formatBucket(newValue?.bucket)}
            ariaLabel="Calculation interval change"
          />
        </ActivityItem.Section>
      )}

      {entityChanged && (
        <ActivityItem.Section label="Entity">
          <ActivityTagItem
            oldValue={
              displayNames.get(oldValue?.entity || '') ||
              oldValue?.entity ||
              'Not set'
            }
            newValue={
              displayNames.get(newValue?.entity || '') ||
              newValue?.entity ||
              'Not set'
            }
            ariaLabel="Entity change"
          />
        </ActivityItem.Section>
      )}

      {assignmentTableChanged && (
        <ActivityItem.Section label="Updated assignment table">
          <ActivityTagItem
            oldValue={
              displayNames.get(oldValue?.assignmentTable || '') ||
              oldValue?.assignmentTable ||
              'Not set'
            }
            newValue={
              displayNames.get(newValue?.assignmentTable || '') ||
              newValue?.assignmentTable ||
              'Not set'
            }
            ariaLabel="Assignment table change"
          />
        </ActivityItem.Section>
      )}

      {exposureMetricChanged && (
        <ActivityItem.Section label="Updated exposure metric">
          <ActivityTagItem
            oldValue={
              displayNames.get(oldValue?.exposureMetric || '') ||
              oldValue?.exposureMetric ||
              'Not set'
            }
            newValue={
              displayNames.get(newValue?.exposureMetric || '') ||
              newValue?.exposureMetric ||
              'Not set'
            }
            ariaLabel="Exposure metric change"
          />
        </ActivityItem.Section>
      )}

      {exposureTableChanged && (
        <ActivityItem.Section label="Updated exposure table">
          <ActivityTagItem
            oldValue={oldValue?.exposureTable ? 'Configured' : 'Not set'}
            newValue={newValue?.exposureTable ? 'Configured' : 'Not set'}
            ariaLabel="Exposure table change"
          />
        </ActivityItem.Section>
      )}

      {scheduleChanges.length > 0 && (
        <ActivityItem.Section label="Updated metric schedule">
          {scheduleChanges.map(({ id }) => (
            <ActivityTagItem
              key={id}
              tagLabel={displayNames.get(id) || id}
              ariaLabel="Metric schedule change"
            />
          ))}
        </ActivityItem.Section>
      )}

      {surfaceOnlyChanges.length > 0 && (
        <ActivityItem.Section label="Updated surface only">
          {surfaceOnlyChanges.map(({ oldMetric, newMetric, id }) => (
            <ActivityTagItem
              key={id}
              tagLabel={displayNames.get(id) || id}
              ariaLabel="Surface only change"
              oldValue={oldMetric.surfaceOnly ? 'Yes' : 'No'}
              newValue={newMetric.surfaceOnly ? 'Yes' : 'No'}
            />
          ))}
        </ActivityItem.Section>
      )}
    </>
  );
};

const metricsChangePredicate = ({
  change,
}: {
  change: ChangeSetFragment['changes'][number];
}) => change.fieldPath === '/module_data/metrics';

export const METRICS_CHANGE_EXTENSION = createWorkflowUpdatesChangeExtension({
  id: 'metrics-change',
  predicate: metricsChangePredicate,
  render: MetricsChangeRender,
});

export { metricsChangePredicate };
